import { useContext, useEffect, useState, type ReactNode } from "react";
import { PenLine } from "lucide-react";
import ModalBase from "components/ModalBase";
import BarraEtapas from "./EstagiosCotacao/BarraEtapas";
import ResumoCotacao from "./EstagiosCotacao/ResumoCotacao";
import Estagio1NoCsv from "./EstagiosCotacao/Estagio1NoCsv";
import { Estagio1Data } from "./EstagiosCotacao/Estagio1";
import Estagio2 from "./EstagiosCotacao/Estagio2";
import Estagio3 from "./EstagiosCotacao/Estagio3";
import { PessoaForm } from "types/PessoaForm";
import { AuthContext } from "context/AuthContext";
import { getClienteAtual } from "services/cliente";

type Props = {
  show: boolean;
  title: string;
  setShowNumber: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

export interface Estagio2Data {
  remetente: PessoaForm;
  destinatario: PessoaForm;
  pagamento: string;
  triangulacao: string;
}

const emptyPessoa: PessoaForm = {
  nome: "",
  country: "",
  state: "",
  city: "",
  rua: "",
  cep: "",
  telefone: "",
};

const ModalRealizarCotacaoNoCsv = ({
  show,
  title,
  setShowNumber,
  logo,
}: Props) => {
  const [estagio, setEstagio] = useState(0);
  const auth = useContext(AuthContext);
  const [estagio1, setEstagio1] = useState<Estagio1Data>({
    currencyPrice: "USD",
    currencyPay: "USD",
    pedidoId: "",
    pesoTotal: "",
    caixaId: "",
    hscodes: [],
  });

  const [estagio2, setEstagio2] = useState<Estagio2Data>({
    remetente: {
      ...emptyPessoa,
    },
    destinatario: {
      ...emptyPessoa,
      nome: auth?.user?.razaoSocial || "",
    },
    pagamento: "",
    triangulacao: "",
  });

  const [temDestinoFixo, setTemDestinoFixo] = useState(false);
  // Motivo do bloqueio, quando houver. Checado na ABERTURA: deixar a pessoa
  // preencher tres etapas para so entao tomar 403 na hora de cotar e perda de
  // tempo dela e chamado para o suporte.
  const [bloqueio, setBloqueio] = useState<string | null>(null);

  useEffect(() => {
    getClienteAtual().then((res) => {
      setBloqueio(
        res.enviosBloqueados
          ? res.enviosBloqueadosMotivo ||
              "Os envios desta conta estao bloqueados. Fale com o suporte da Intrex."
          : null,
      );

      const temDestinoFixo = !!res.destinoFixoRua;
      setTemDestinoFixo(temDestinoFixo);

      setEstagio2({
        remetente: res.remetenteFixoAtivo
          ? {
              nome: res.razaoSocial,
              country: res.enderecoPais,
              state: res.enderecoEstado,
              city: res.enderecoCidade,
              rua: res.enderecoRua,
              cep: res.enderecoCEP,
              telefone: res.telefoneCelular,
            }
          : {
              ...emptyPessoa,
              nome: res.razaoSocial,
              telefone: res.telefoneCelular,
            },
        destinatario: temDestinoFixo
          ? {
              nome: res.destinoFixoNome || auth?.user?.razaoSocial || "",
              country: res.destinoFixoPais || "",
              state: res.destinoFixoEstado || "",
              city: res.destinoFixoCidade || "",
              rua: res.destinoFixoRua || "",
              cep: res.destinoFixoCEP || "",
              telefone: res.destinoFixoTelefone || "",
            }
          : {
              ...emptyPessoa,
              nome: auth?.user?.razaoSocial || "",
            },
        pagamento: "",
        triangulacao: "",
      });
    });
  }, []);

  useEffect(() => {
    if (!estagio1.selectedPedido) return;
    if (temDestinoFixo) return; // destino fixo do cliente tem prioridade (não há dados reais de pedido nesse fluxo manual)

    const pedido = estagio1.selectedPedido;

    setEstagio2((prev) => ({
      ...prev,
      destinatario: {
        nome: pedido.nomeComprador ?? "",
        country: pedido.pais ?? "",
        state: pedido.estado ?? "",
        city: pedido.cidade ?? "",
        rua: pedido.endereco ?? "",
        cep: pedido.CEP ?? "",
        telefone: pedido.telefoneComprador ?? "",
      },
    }));
  }, [estagio1.selectedPedido, temDestinoFixo]);

  const next = () => setEstagio((prev) => prev + 1);
  const prev = () => setEstagio((prev) => prev - 1);

  useEffect(() => {
    if (!show) {
      setEstagio(0);
    }
  }, [show]);

  const setShow = (value: React.SetStateAction<boolean>) => {
    const nextValue = typeof value === "function" ? value(show) : value;
    if (!nextValue) setShowNumber(-1);
  };

  return (
    <ModalBase
      show={show}
      onClose={() => setShowNumber(-1)}
      titulo={title}
      icone={logo ?? <PenLine />}
      subtitulo="Para envios que não vêm de uma loja integrada"
      largura="1060px"
      corpoSemPadding
    >
      {bloqueio ? (
        <div className="mx-auto max-w-lg px-6 py-12 text-center">
          <p className="mb-2 text-base font-semibold text-slate-900">
            Envios bloqueados nesta conta
          </p>
          <p className="text-sm text-slate-500">{bloqueio}</p>
          <p className="mt-4 text-xs text-slate-400">
            Você continua com acesso aos envios já feitos e pode baixar as etiquetas e
            invoices deles normalmente.
          </p>
        </div>
      ) : (
        <>
          <BarraEtapas etapas={ETAPAS} atual={estagio} />

          <div className="grid grid-cols-1 md:grid-cols-[1fr_250px]">
            {/* Os três ficam montados e escondidos, não renderizados por
                condição: o Estágio 1 guarda caixas e itens em estado local, e
                desmontá-lo ao avançar apagaria tudo que a pessoa digitou. */}
            <div className="min-w-0 px-6 py-5">
              <div className={estagio === 0 ? "" : "hidden"}>
                <Estagio1NoCsv
                  aberto={show}
                  setShow={setShow}
                  next={next}
                  data={estagio1}
                  setData={setEstagio1}
                />
              </div>
              <div className={estagio === 1 ? "" : "hidden"}>
                <Estagio2
                  previous={prev}
                  next={next}
                  data={estagio2}
                  setData={setEstagio2}
                />
              </div>
              <div className={estagio === 2 ? "" : "hidden"}>
                <Estagio3
                  previous={prev}
                  estagio1={estagio1}
                  estagio2={estagio2}
                  estagio={estagio}
                  setShow={setShow}
                  abrirCarteira={() => setShowNumber(14)}
                />
              </div>
            </div>

            <ResumoCotacao
              estagio1={estagio1}
              destino={{
                cidade: estagio2.destinatario.city,
                estado: estagio2.destinatario.state,
                pais: estagio2.destinatario.country,
              }}
              onAdicionarSaldo={() => setShowNumber(14)}
            />
          </div>
        </>
      )}
    </ModalBase>
  );
};

export default ModalRealizarCotacaoNoCsv;

const ETAPAS = [
  { titulo: "Envio", apoio: "Caixas e itens" },
  { titulo: "Dados", apoio: "Remetente e destinatário" },
  { titulo: "Frete", apoio: "Escolha e invoice" },
];
